import { useDisclosure } from '@chakra-ui/react'
import type { Abi, Address } from '@zircuit/zircuit-viem'
import React from 'react'
import { useAccount } from 'wagmi'
import { EIP7702_DELEGATION_PREFIX } from 'constants/common'
import { createWalletClientL2, publicClientL2 } from 'lib/viem'
import type { AbiInputWithValue } from 'utils/getZodSchemaFromAbiInputs'

export type UserOperation = {
  functionName: string
  id: string
  target: Address
  payableAmount: string
  abi: Abi
  data: AbiInputWithValue[]
}

type UserOperationContextType = {
  userOperations: UserOperation[]
  onAddUserOperation: (userOperation: UserOperation) => void
  onRemoveUserOperation: (id: string) => void
  onUpdateUserOperation: (userOperation: UserOperation) => void
  onSuccessfulRelaying: () => void
  hasEnabled7702Relayer: boolean
  setHasEnabled7702Relayer: (enabled: boolean) => void
  isGetting7702RelayerStatus: boolean
  isDrawerOpen: boolean
  onDrawerOpen: () => void
  onDrawerClose: () => void
}

const UserOperationContext = React.createContext<UserOperationContextType>({
  userOperations: [],
  onAddUserOperation: () => {},
  onRemoveUserOperation: () => {},
  onUpdateUserOperation: () => {},
  onSuccessfulRelaying: () => {},
  hasEnabled7702Relayer: false,
  setHasEnabled7702Relayer: () => {},
  isGetting7702RelayerStatus: true,
  isDrawerOpen: false,
  onDrawerOpen: () => {},
  onDrawerClose: () => {},
})

const useUserOperationContext = () => React.useContext(UserOperationContext)

type UserOperationProviderProps = {
  children: React.ReactNode
}

const UserOperationProvider = ({ children }: UserOperationProviderProps) => {
  const [userOperations, setUserOperations] = React.useState<UserOperation[]>([])
  const [hasEnabled7702Relayer, setHasEnabled7702Relayer] = React.useState<boolean>(false)
  const [isGetting7702RelayerStatus, setIsGetting7702RelayerStatus] = React.useState<boolean>(true)

  const { address } = useAccount()
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  const handleAddUserOperation = (userOperation: UserOperation) => {
    setUserOperations((prev) => {
      const updatedOperations = [...prev, userOperation]
      localStorage.setItem('userOperations', JSON.stringify(updatedOperations))

      return updatedOperations
    })
  }

  const handleRemoveUserOperation = (id: string) => {
    setUserOperations((prev) => {
      const filteredOperations = prev.filter((operation) => operation.id !== id)
      localStorage.setItem('userOperations', JSON.stringify(filteredOperations))

      return filteredOperations
    })
  }

  const handleUpdateUserOperation = (userOperation: UserOperation) => {
    setUserOperations((prev) => {
      const updatedOperations = prev.map((operation) =>
        operation.id === userOperation.id ? userOperation : operation
      )

      localStorage.setItem('userOperations', JSON.stringify(updatedOperations))
      return updatedOperations
    })
  }

  const handleSuccessfulRelaying = () => {
    setUserOperations(() => {
      localStorage.removeItem('userOperations')
      return []
    })
  }

  React.useEffect(() => {
    if (userOperations.length === 0) {
      onDrawerClose()
    }
  }, [userOperations, onDrawerClose])

  React.useEffect(() => {
    const userOps = localStorage.getItem('userOperations')

    if (userOps) {
      setUserOperations(JSON.parse(userOps))
    }
  }, [])

  React.useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'userOperations') {
        const updatedUserOperations = JSON.parse(event.newValue || '[]')
        setUserOperations(updatedUserOperations)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  React.useEffect(() => {
    const asyncFn = async () => {
      try {
        const walletClient = await createWalletClientL2()

        const currentCode = await publicClientL2.getCode({
          address: walletClient!.account!.address,
        })

        const hasRelayer = currentCode?.startsWith(EIP7702_DELEGATION_PREFIX) ?? false

        setHasEnabled7702Relayer(hasRelayer)
        localStorage.setItem('has7702RelayerEnabled', hasRelayer.toString())
      } catch {
        // SENTRYFIXME: Log in sentry
        setHasEnabled7702Relayer(false)
        localStorage.setItem('has7702RelayerEnabled', 'false')
      } finally {
        setIsGetting7702RelayerStatus(false)
      }
    }

    void asyncFn()
  }, [address])

  const contextValue = React.useMemo(
    () => ({
      userOperations,
      onAddUserOperation: handleAddUserOperation,
      onRemoveUserOperation: handleRemoveUserOperation,
      onUpdateUserOperation: handleUpdateUserOperation,
      onSuccessfulRelaying: handleSuccessfulRelaying,
      hasEnabled7702Relayer,
      setHasEnabled7702Relayer,
      isGetting7702RelayerStatus,
      onDrawerOpen,
      onDrawerClose,
      isDrawerOpen,
    }),
    [
      hasEnabled7702Relayer,
      isDrawerOpen,
      isGetting7702RelayerStatus,
      onDrawerClose,
      onDrawerOpen,
      userOperations,
    ]
  )

  return (
    <UserOperationContext.Provider value={contextValue}>{children}</UserOperationContext.Provider>
  )
}

export { UserOperationProvider, useUserOperationContext }
